import { useState } from 'react'
import type { JSX } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { ApprovalsForm } from '@/settings/approvals/form'
import type { GatingBody } from '@/settings/approvals/form'
import { TargetSetup } from '@/settings/publish-target/TargetSetup'
import { scheduleBody } from '@/settings/run-schedule/cadence'
import type { Schedule } from '@/settings/run-schedule/cadence'
import { RunScheduleForm } from '@/settings/run-schedule/form'
import type { SiteRow } from '@/settings/site'
import { UptimeForm } from '@/settings/uptime/form'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Empty, ErrorState, Loading } from '@/ui/states'

const STEPS = ['run_schedule', 'uptime', 'publish_target', 'approvals'] as const
type Step = (typeof STEPS)[number]
const TITLES: Record<Step, string> = {
  run_schedule: 'Run schedule',
  uptime: 'Uptime checks',
  publish_target: 'Publish target',
  approvals: 'Approvals',
}

/**
 * The per-site wizard steps, for a site added after sign-up — four since
 * batch 2 added Uptime (2026-09-15).
 *
 * Settings consolidation (2026-09-15). `SiteStep` lands here when the wizard
 * was already complete, so a second site is not left half-configured. Client
 * side only: every Continue writes the real resource, so closing the tab
 * leaves the site on API defaults — the same outcome as Skip — and there is
 * nothing to resume. The onboarding tables are not touched.
 *
 * Addressed by domain rather than id because `PUT /onboarding/site` answers
 * with the wizard's *first* site, not the one just added. The API normalises
 * what was typed (`app/onboarding/domains.py` — lower-case, no scheme, no
 * `www.`), so the lookup does the same before giving up.
 */
export function SiteSetup(): JSX.Element {
  const { domain = '' } = useParams()
  const navigate = useNavigate()
  const { tenant, membership } = useActiveTenant()
  const [index, setIndex] = useState(0)
  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const wanted = normalise(decodeURIComponent(domain))
  const site =
    (sites.state.data?.sites ?? []).find((row) => normalise(row.domain) === wanted) ?? null

  function advance(): void {
    if (index + 1 >= STEPS.length) {
      void navigate('/seo/sites')
      return
    }
    setIndex(index + 1)
  }

  if (sites.state.status === 'loading') return <Loading label="Loading your sites" />
  if (sites.state.status === 'error') {
    return <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
  }
  if (site === null) {
    return (
      <Empty
        title="No such site"
        body="It may have been removed, or the address was mistyped."
        action={
          <Link className="ar-btn ar-btn--sm" to="/seo/sites">
            Back to Sites
          </Link>
        }
      />
    )
  }

  const step = STEPS[index] ?? 'run_schedule'
  return (
    <div className="app-wiz">
      <div className="app-wiz__progress">
        <div className="app-wiz__progress-label">
          <span>
            Step {index + 1} of {STEPS.length} · {TITLES[step]}
          </span>
          <span>{site.domain}</span>
        </div>
        <div className="ar-meter">
          <div
            className="ar-meter__fill"
            style={{ width: `${String(Math.floor(((index + 1) * 100) / STEPS.length))}%` }}
          />
        </div>
      </div>
      <section className="ar-card">
        <div className="ar-card__body ar-stack">
          {step === 'run_schedule' ? (
            <SiteRunSchedule
              key={site.id}
              site={site}
              automationAvailable={tenant.automation_available}
              onDone={advance}
            />
          ) : null}
          {step === 'uptime' ? <SiteUptime key={site.id} site={site} onDone={advance} /> : null}
          {step === 'publish_target' ? (
            <TargetSetup
              key={site.id}
              siteId={site.id}
              domain={site.domain}
              configurable={may(membership, 'tenant:update')}
              submitLabel="Continue"
              onSaved={advance}
            />
          ) : null}
          {step === 'approvals' ? (
            <SiteApprovals key={site.id} site={site} onDone={advance} />
          ) : null}
        </div>
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Button tone="quiet" onClick={advance}>
            Skip — keep the default
          </Button>
        </div>
      </section>
    </div>
  )
}

function normalise(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/[/?#].*$/, '')
}

function SiteRunSchedule({
  site,
  automationAvailable,
  onDone,
}: {
  site: SiteRow
  automationAvailable: boolean
  onDone: () => void
}): JSX.Element {
  const schedule = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/run-schedules/{site_id}', {
          params: { path: { site_id: site.id } },
          signal,
        }),
      ),
    [site.id],
  )
  const save = useMutation((body: Schedule) =>
    call(() =>
      api.PUT('/api/v1/seo/run-schedules/{site_id}', {
        params: { path: { site_id: body.site_id } },
        body: scheduleBody(body),
      }),
    ),
  )
  if (schedule.state.status === 'loading') return <Loading label="Loading" lines={2} />
  if (schedule.state.status === 'error') {
    return <ErrorState failure={schedule.state.failure} onRetry={schedule.refetch} />
  }
  return (
    <RunScheduleForm
      schedule={schedule.state.data}
      domain={site.domain}
      automationAvailable={automationAvailable}
      submitLabel="Continue"
      onSubmit={async (next) => {
        const result = await save.run(next)
        if (result.ok) onDone()
        return result
      }}
    />
  )
}

function SiteUptime({ site, onDone }: { site: SiteRow; onDone: () => void }): JSX.Element {
  const uptime = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/seo/sites/{site_id}/uptime', {
          params: { path: { site_id: site.id } },
          signal,
        }),
      ),
    [site.id],
  )
  const save = useMutation((body: Schema<'MonitorIn'>) =>
    call(() =>
      api.PUT('/api/v1/seo/sites/{site_id}/uptime', {
        params: { path: { site_id: site.id } },
        body,
      }),
    ),
  )
  const stop = useMutation(() =>
    call(() =>
      api.DELETE('/api/v1/seo/sites/{site_id}/uptime', {
        params: { path: { site_id: site.id } },
      }),
    ),
  )
  if (uptime.state.status === 'loading') return <Loading label="Loading" lines={2} />
  if (uptime.state.status === 'error') {
    return <ErrorState failure={uptime.state.failure} onRetry={uptime.refetch} />
  }
  const monitor = uptime.state.data.monitor
  return (
    <UptimeForm
      monitor={monitor}
      domain={site.domain}
      submitLabel="Continue"
      onSubmit={async (choice) => {
        // *Off* on a site that was never watched is the default: nothing to delete.
        const result =
          choice.kind === 'off'
            ? monitor === null
              ? { ok: true as const, data: undefined }
              : await stop.run(undefined)
            : await save.run({
                url: choice.url,
                enabled: choice.enabled,
                interval_seconds: choice.seconds,
              })
        if (result.ok) onDone()
        return result
      }}
    />
  )
}

function SiteApprovals({ site, onDone }: { site: SiteRow; onDone: () => void }): JSX.Element {
  const gating = useQuery(
    (signal) => call(() => api.GET('/api/v1/seo/approvals/sites/gating', { signal })),
    [],
  )
  const save = useMutation((body: GatingBody) =>
    call(() =>
      api.PUT('/api/v1/seo/approvals/sites/{site_id}/gating', {
        params: { path: { site_id: site.id } },
        body,
      }),
    ),
  )
  if (gating.state.status === 'loading') return <Loading label="Loading" lines={2} />
  if (gating.state.status === 'error') {
    return <ErrorState failure={gating.state.failure} onRetry={gating.refetch} />
  }
  const row = (gating.state.data.sites ?? []).find((entry) => entry.site_id === site.id) ?? null
  if (row === null) {
    return (
      <p className="ar-muted" style={{ margin: 0 }}>
        Nothing to decide yet for {site.domain} — everything is approval-gated by default.
      </p>
    )
  }
  return (
    <ApprovalsForm
      site={row}
      submitLabel="Continue"
      onSubmit={async (body) => {
        const result = await save.run(body)
        if (result.ok) onDone()
        return result
      }}
    />
  )
}
